import { ButtonBase, Stack, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useState, useRef, useEffect, MouseEvent } from 'react';
import { findIndex, map } from 'lodash';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';

import SideBarItem, { SideBarItemData } from '@/components/ui/SideBarItem';
import Icon from '@/components/ui/Icon';
import SideBarHoverTooltip, {
  SideBarHoverTooltipRef,
} from '@/components/ui/SideBarHoverTooltip';
import {
  SidebarLogo,
  SidebarRahmen,
  SidebarUmschalter,
  SidebarZurueck,
} from '@/components/ui/Rahmen';

import style from './style';
import sideBarItemStyle from '@/components/ui/SideBarItem/style';
import { SXStyleSheet } from '@/utils';
import { Routes } from '@/constants/routes';
import {
  SideBarIconLabels,
  getSubItemsForIcon,
  getParentRouteForIcon,
} from '@/constants/sidebar';
import useSidebarStore from '@/store/sidebar';

export interface SideBarProps {
  items: SideBarItemData[];
  endElement?: JSX.Element | false;
  altColor?: boolean;
  backLabel?: string;
  onGoBack?: () => void;
  onSideBarItemClick?: (item: SideBarItemData) => void;
  showTooltip?: boolean;
  collapsible?: boolean;
  /** When true, all clicks call onSideBarItemClick instead of navigating to sub-routes */
  disableNavigation?: boolean;
  /** Optional callback for sub-item clicks when disableNavigation is true.
   *  Receives the sub-item path and parent icon. If not set, sub-item clicks
   *  fall back to calling onSideBarItemClick with the parent item. */
  onSubItemScrollClick?: (subItemPath: string, parentIcon: string) => void;
}

const SIDEBAR_ITEM_MB = 1;

const SideBar: FC<SideBarProps> = ({
  items,
  endElement,
  altColor,
  backLabel,
  onGoBack,
  onSideBarItemClick,
  showTooltip = true,
  collapsible = true,
  disableNavigation = false,
  onSubItemScrollClick,
}) => {
  const { isExpanded, toggleSidebar, expandedGroups, toggleGroup } =
    useSidebarStore();
  const navigate = useNavigate();
  /**
   * 🔥 Die Query gehoert an die Ziel-URL (Entscheid Alex 17.08.2026, Teil 1).
   * Unter /home/customers/customer/ steht der Kunde ausschliesslich in
   * `?id=` — und seit dem Reaktivierungs-Gate zusaetzlich der Einstiegs-Fall
   * in `&case=`. Dieser Baustein hat die Ziel-URL bisher neu zusammengesetzt
   * und die Query dabei verworfen; der Kontext starb also beim ersten Klick
   * in der Sidebar.
   */
  const location = useLocation();
  const paths = useMatches();
  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(
    undefined,
  );
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const [tooltipTop, setTooltipTop] = useState<number | undefined>(undefined);
  const resetTooltipStateRef = useRef<SideBarHoverTooltipRef | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const sidebarExpanded = collapsible && isExpanded;
  const showHoverTooltip = showTooltip && !sidebarExpanded;

  const isActive = useCallback(
    (item: SideBarItemData) => {
      const path = item.path;
      if (path === Routes.Root) return false;

      const segmentMatch = (pathname: string, segment: string) =>
        pathname.endsWith(`/${segment}`) ||
        pathname.includes(`/${segment}/`);

      const directMatch =
        findIndex(paths, (match) => segmentMatch(match.pathname, path)) >= 0;
      if (directMatch) return true;

      const subItems = getSubItemsForIcon(item.icon || '');
      return subItems.some((sub) =>
        paths.some((match) => segmentMatch(match.pathname, sub.path)),
      );
    },
    [paths],
  );

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const startHideTimeout = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShouldShowTooltip(false);
      setHoveredIndex(undefined);
    }, 400);
  }, [clearHideTimeout]);

  const handleMouseEnter = useCallback(() => {
    if (!showHoverTooltip) return;
    setShouldShowTooltip(true);
    clearHideTimeout();
  }, [clearHideTimeout, showHoverTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (!showHoverTooltip) return;
    startHideTimeout();
  }, [startHideTimeout, showHoverTooltip]);

  const handleTooltipMouseEnter = useCallback(() => {
    if (!showHoverTooltip) return;
    setShouldShowTooltip(true);
    clearHideTimeout();
  }, [clearHideTimeout, showHoverTooltip]);

  const handleTooltipMouseLeave = useCallback(() => {
    if (!showHoverTooltip) return;
    startHideTimeout();
  }, [startHideTimeout, showHoverTooltip]);

  const handleSideBarHover = useCallback(() => {}, []);

  const handleItemMouseEnter = useCallback(
    (index: number, e: MouseEvent<HTMLElement>) => {
      if (!showHoverTooltip) return;
      // Tooltip am tatsaechlich gehoverten Item verankern: vertikale Item-Mitte
      // relativ zur Sidebar messen (robust gegen Layout-/Offset-Aenderungen).
      const sidebarEl = sidebarRef.current;
      if (sidebarEl) {
        const itemRect = e.currentTarget.getBoundingClientRect();
        const sidebarRect = sidebarEl.getBoundingClientRect();
        setTooltipTop(itemRect.top - sidebarRect.top + itemRect.height / 2);
      }
      setShouldShowTooltip(true);
      setHoveredIndex(index);
    },
    [showHoverTooltip],
  );

  const handleSubItemClick = useCallback(
    (subItemPath: string, mainItemIcon: string) => {
      if (disableNavigation) {
        // Use dedicated scroll callback if provided
        if (onSubItemScrollClick) {
          onSubItemScrollClick(subItemPath, mainItemIcon);
        } else if (onSideBarItemClick) {
          // Fallback: scroll to parent section
          const parentItem = items.find((i) => i.icon === mainItemIcon);
          if (parentItem) {
            onSideBarItemClick(parentItem);
          }
        }
        // Ensure group stays expanded
        if (!expandedGroups.includes(mainItemIcon)) {
          toggleGroup(mainItemIcon);
        }
        return;
      }
      const parentPath = getParentRouteForIcon(mainItemIcon);
      const fullPath = parentPath
        ? `/${Routes.Home}/${Routes.Customers}/${Routes.Customer}/${parentPath}/${subItemPath}${location.search}`
        : '';
      if (fullPath) {
        navigate(fullPath);
      }
    },
    [disableNavigation, onSubItemScrollClick, onSideBarItemClick, items, expandedGroups, toggleGroup, navigate, location.search],
  );

  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  const isBackButtonVisible = useMemo(() => {
    return !!onGoBack && !!backLabel;
  }, [backLabel, onGoBack]);

  const hasMandantenButton = false; // back button now visible for all roles

  const renderItems = (item: SideBarItemData, i: number) => {
    const iconKey = item.icon || '';
    const label = item.label || SideBarIconLabels[iconKey] || '';
    const subItems = item.label ? [] : getSubItemsForIcon(iconKey);
    const isGroupExpanded = expandedGroups.includes(iconKey);

    return (
      <SideBarItem
        item={item}
        key={item.path}
        active={isActive(item)}
        altColor={altColor}
        isExpanded={sidebarExpanded}
        label={label}
        subItems={subItems}
        isGroupExpanded={isGroupExpanded}
        onSideBarItemClick={onSideBarItemClick}
        onToggleGroup={() => toggleGroup(iconKey)}
        onSubItemClick={handleSubItemClick}
        onMouseEnter={
          showHoverTooltip
            ? (e) =>
                handleItemMouseEnter(
                  hasMandantenButton ? i + 1 : i,
                  e,
                )
            : undefined
        }
      />
    );
  };

  const tooltipLabels = useMemo(() => {
    const labels = items.map((item) => item.label || SideBarIconLabels[item.icon || ''] || '');
    if (hasMandantenButton) {
      return [SideBarIconLabels['Users'], ...labels];
    }
    return labels;
  }, [items, hasMandantenButton]);

  const tooltipItems = useMemo(() => {
    if (hasMandantenButton) {
      return [{ icon: 'Users', path: Routes.Customers }, ...items];
    }
    return items;
  }, [items, hasMandantenButton]);

  return (
    <SidebarRahmen
      ref={sidebarRef}
      ausgeklappt={sidebarExpanded}
      ariaLabel="Hauptnavigation"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarLogo ausgeklappt={sidebarExpanded} />
      {collapsible && (
        <SidebarUmschalter ausgeklappt={sidebarExpanded} onClick={toggleSidebar} />
      )}
      {isBackButtonVisible && (
        <SidebarZurueck
          ausgeklappt={sidebarExpanded}
          icon="Home"
          label={backLabel!}
          onClick={onGoBack!}
        />
      )}
      <Stack
        sx={SXStyleSheet.merge(
          style.itemsContainer,
          isBackButtonVisible && style.noLogo,
          collapsible && { paddingTop: '16px' },
        )}
      >
        {hasMandantenButton && (
          <Stack
            mb={SIDEBAR_ITEM_MB}
          >
            <Stack
              sx={SXStyleSheet.merge(sideBarItemStyle.container)}
              onMouseEnter={() => {
                if (showHoverTooltip) {
                  setShouldShowTooltip(true);
                  setHoveredIndex(0);
                }
              }}
              onMouseLeave={() => {
                if (showHoverTooltip) {
                  startHideTimeout();
                }
              }}
            >
              <ButtonBase
                sx={sideBarItemStyle.buttonContainer}
                onClick={onGoBack}
                disableRipple
              >
                <Icon
                  icon="Users"
                  size={20}
                  sx={{ color: 'navigation.text' }}
                />
                {sidebarExpanded && (
                  <Typography variant="wertBetont" sx={sideBarItemStyle.labelText}>
                    Mandanten
                  </Typography>
                )}
              </ButtonBase>
            </Stack>
          </Stack>
        )}
        {map(items, renderItems)}
      </Stack>
      {!!endElement && (
        <Stack alignItems="center" justifyContent="center" width="100%">
          {endElement}
        </Stack>
      )}
      {showHoverTooltip && (
        <SideBarHoverTooltip
          isVisible={shouldShowTooltip}
          labels={tooltipLabels}
          activeIndex={hoveredIndex}
          top={tooltipTop}
          items={tooltipItems}
          hasBackButton={isBackButtonVisible}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onSideBarHover={handleSideBarHover}
          ref={resetTooltipStateRef}
        />
      )}
    </SidebarRahmen>
  );
};

export default SideBar;
